import React, { useEffect, useState } from 'react';
import { Box, Chip, Collapse, Stack, alpha, useTheme } from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { type SettingInfoDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import { CaptionText } from '../../../shared/ui/typography';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import { getWorkflowWorkspacePalette } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { borderRadius, layoutGap } from '../../../theme/theme';
import { useProfileContents } from '../hooks/useProfileContents';
import {
  getProfileParameterKindLabel,
  type ProfileParameterKind,
} from '../lib/profileContents';

interface ProfileContentsSectionProps {
  profile: SettingInfoDto | null;
}

const SOURCE_LABELS: Record<string, string> = {
  mappingTable: 'mapping table',
  connector: 'connector binding',
  bolt: 'bolt',
  process: 'process scope',
  project: 'project scope',
  additional: 'additional categories',
  scopeCustom: 'custom scope row',
};

/**
 * Read-only preview of the parameters a profile references, grouped by how
 * each parameter is bound (project or family, type or instance, and system),
 * so large profiles can be inspected before they are loaded.
 */
export default function ProfileContentsSection({
  profile,
}: ProfileContentsSectionProps): React.ReactElement | null {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const [expanded, setExpanded] = useState(false);
  const { contents, isLoading, error } = useProfileContents(profile);
  const profileKey = profile ? `${profile.name}::${profile.settingsType}` : null;

  useEffect(() => {
    setExpanded(false);
  }, [profileKey]);

  if (!profile) {
    return null;
  }

  const headerLabel = isLoading
    ? `Reading parameters saved in “${profile.name}”…`
    : contents
      ? `Saved profile parameters — ${contents.totalCount} parameter reference${contents.totalCount === 1 ? '' : 's'}`
      : 'Saved profile parameters';

  const getGroupLabel = (kind: ProfileParameterKind, count: number): string =>
    `${getProfileParameterKindLabel(kind)} (${count})`;

  return (
    <Box
      sx={{
        border: `1px solid ${workspacePalette.sectionBorder}`,
        borderRadius: `${borderRadius.sm}px`,
        bgcolor: workspacePalette.sectionBackground,
        px: 0.85,
        py: 0.45,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={layoutGap.row}
        component="button"
        type="button"
        data-testid="profile-contents-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        sx={{
          width: '100%',
          border: 'none',
          bgcolor: 'transparent',
          p: 0,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <CaptionText sx={{ color: workspacePalette.headingText }}>{headerLabel}</CaptionText>
        {isLoading ? (
          <OperationProgressIndicator size={12} ariaLabel={`Reading parameters saved in ${profile.name}`} />
        ) : expanded ? (
          <ExpandLessIcon fontSize="small" sx={{ color: workspacePalette.tertiaryText }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ color: workspacePalette.tertiaryText }} />
        )}
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pt: 0.5, pb: 0.25 }}>
          {error ? (
            <CaptionText sx={{ color: theme.palette.warning.dark }}>PipeEX could not read this profile: {error} Refresh the profile list, then try again.</CaptionText>
          ) : null}
          {!error && contents && contents.totalCount === 0 ? (
            <CaptionText sx={{ color: workspacePalette.tertiaryText }}>
              This profile has no saved parameter references to compare with the active Revit project.
            </CaptionText>
          ) : null}
          {!error && contents
            ? contents.groups.map((group) => (
                <Box key={group.kind} data-testid={`profile-contents-group-${group.kind}`} sx={{ mb: 0.6 }}>
                  <CaptionText
                    sx={{
                      color:
                        group.kind === 'unknown'
                          ? theme.palette.warning.dark
                          : workspacePalette.tertiaryText,
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    {getGroupLabel(group.kind, group.entries.length)}
                  </CaptionText>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      maxHeight: 180,
                      overflowY: 'auto',
                    }}
                  >
                    {group.entries.map((entry) => (
                      <Chip
                        key={entry.name}
                        size="small"
                        variant="outlined"
                        label={entry.name}
                        title={`${entry.name} — used by: ${entry.sources
                          .map((source) => SOURCE_LABELS[source] ?? source)
                          .join(', ')}`}
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          borderColor: alpha(theme.palette.text.primary, 0.16),
                          color: workspacePalette.bodyText,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))
            : null}
        </Box>
      </Collapse>
    </Box>
  );
}
